/**
 * Dense linear algebra for small systems.
 *
 * Sized for the constraint solver, where a cluster is a handful of points and the normal
 * equations are at most a few dozen unknowns. Everything is stored row-major in a flat
 * `Float64Array`: at these sizes an array-of-arrays costs more in pointer chasing and
 * allocation than the arithmetic itself, and the solver runs inside a drag loop.
 *
 * No general-purpose matrix library is pulled in for the same reason the rendering engine
 * is hand-written — the operations needed are three, and a dependency would bring a
 * thousand that are not.
 */

export interface Matrix {
  readonly rows: number;
  readonly cols: number;
  readonly data: Float64Array;
}

export function createMatrix(rows: number, cols: number): Matrix {
  return { rows, cols, data: new Float64Array(rows * cols) };
}

export function at(m: Matrix, row: number, col: number): number {
  return m.data[row * m.cols + col] ?? 0;
}

export function set(m: Matrix, row: number, col: number, value: number): void {
  m.data[row * m.cols + col] = value;
}

export function addTo(m: Matrix, row: number, col: number, value: number): void {
  m.data[row * m.cols + col] = (m.data[row * m.cols + col] ?? 0) + value;
}

/**
 * `Jᵀ·J`, the Gauss–Newton approximation to the Hessian.
 *
 * Only the lower triangle is filled and then mirrored: the product is symmetric by
 * construction, and computing both halves would double the cost of the hottest step in
 * the solver.
 */
export function normalMatrix(jacobian: Matrix): Matrix {
  const n = jacobian.cols;
  const result = createMatrix(n, n);

  for (let i = 0; i < n; i++) {
    for (let j = 0; j <= i; j++) {
      let sum = 0;
      for (let k = 0; k < jacobian.rows; k++) {
        sum += at(jacobian, k, i) * at(jacobian, k, j);
      }
      set(result, i, j, sum);
      set(result, j, i, sum);
    }
  }
  return result;
}

/** `Jᵀ·v`. */
export function transposeMultiplyVector(jacobian: Matrix, v: Float64Array): Float64Array {
  const result = new Float64Array(jacobian.cols);
  for (let col = 0; col < jacobian.cols; col++) {
    let sum = 0;
    for (let row = 0; row < jacobian.rows; row++) {
      sum += at(jacobian, row, col) * (v[row] ?? 0);
    }
    result[col] = sum;
  }
  return result;
}

/**
 * Solves `A·x = b` for a symmetric positive-definite `A`, by Cholesky decomposition.
 *
 * Returns `null` when `A` is not positive definite — which for the solver is not an
 * error but information: it means the damping is too small for the current step, and
 * Levenberg–Marquardt responds by increasing it and retrying. Throwing here would turn a
 * normal iteration into a crash.
 *
 * `A` is decomposed in place into a scratch copy, so the caller's matrix is untouched.
 */
export function solveSymmetric(a: Matrix, b: Float64Array): Float64Array | null {
  const n = a.rows;
  if (n !== a.cols || b.length !== n) return null;

  // L, lower triangular, such that A = L·Lᵀ.
  const l = new Float64Array(n * n);

  for (let i = 0; i < n; i++) {
    for (let j = 0; j <= i; j++) {
      let sum = at(a, i, j);
      for (let k = 0; k < j; k++) {
        sum -= (l[i * n + k] ?? 0) * (l[j * n + k] ?? 0);
      }

      if (i === j) {
        // A non-positive pivot means A is not positive definite.
        if (sum <= 0) return null;
        l[i * n + j] = Math.sqrt(sum);
      } else {
        const pivot = l[j * n + j] ?? 0;
        if (pivot === 0) return null;
        l[i * n + j] = sum / pivot;
      }
    }
  }

  // Forward substitution: L·y = b
  const y = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    let sum = b[i] ?? 0;
    for (let k = 0; k < i; k++) {
      sum -= (l[i * n + k] ?? 0) * (y[k] ?? 0);
    }
    y[i] = sum / (l[i * n + i] ?? 1);
  }

  // Back substitution: Lᵀ·x = y
  const x = new Float64Array(n);
  for (let i = n - 1; i >= 0; i--) {
    let sum = y[i] ?? 0;
    for (let k = i + 1; k < n; k++) {
      sum -= (l[k * n + i] ?? 0) * (x[k] ?? 0);
    }
    x[i] = sum / (l[i * n + i] ?? 1);
  }

  return x;
}

/** Sum of squares — the objective Levenberg–Marquardt minimises. */
export function sumOfSquares(v: Float64Array): number {
  let total = 0;
  for (const value of v) total += value * value;
  return total;
}

/** Largest absolute entry. Used as the convergence test on the gradient. */
export function maxAbs(v: Float64Array): number {
  let largest = 0;
  for (const value of v) {
    const magnitude = Math.abs(value);
    if (magnitude > largest) largest = magnitude;
  }
  return largest;
}
